import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Transition,
  Dialog, DialogPanel, DialogTitle,
  Checkbox,
  Field, Label, Input,
} from '@headlessui/react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, ENDPOINTS } from '../config/api';
import { LOGO_URL } from '../config/brand';
import { Building2, CreditCard, ShieldCheck, Check, Mail } from 'lucide-react';

// ── Floating grid dots ────────────────────────────────────────────────────────
const GridDots = () => (
  <div className="absolute inset-0 grid grid-cols-10 gap-0 pointer-events-none overflow-hidden" aria-hidden="true">
    {Array.from({ length: 80 }).map((_, i) => (
      <div key={i} className="w-1 h-1 rounded-full bg-blue-500/10 mx-auto my-7 animate-pulse"
        style={{ animationDelay: `${(i * 0.13) % 4}s`, animationDuration: '4s' }} />
    ))}
  </div>
);

// ── Stat counter ──────────────────────────────────────────────────────────────
function AnimatedStat({ target, label, prefix = '', suffix = '' }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      const num = parseFloat(target.replace(/[^0-9.]/g, ''));
      const dur = 1400;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / dur, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        setVal(Math.round(num * ease * 10) / 10);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return (
    <div ref={ref} className="space-y-1">
      <div className="text-xl sm:text-2xl font-extrabold text-white font-mono tracking-tight">{prefix}{val}{suffix}</div>
      <div className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest">{label}</div>
    </div>
  );
}

const ErrorBox = ({ children, shake }) => (
  <div className={`p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs leading-relaxed ${shake ? 'animate-shake' : ''}`}>{children}</div>
);

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin text-white flex-shrink-0" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity=".25" />
    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const PwInput = ({ showPw, onToggle, ...props }) => (
  <div className="relative">
    <Input as="input" type={showPw ? 'text' : 'password'}
      className="w-full px-3.5 py-2.5 pr-12 bg-zinc-900/50 border border-white/5 focus:border-blue-500/50 rounded-lg text-sm text-white placeholder-zinc-650 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all font-sans"
      {...props} />
    <button type="button" onClick={onToggle} tabIndex={-1}
      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px] font-mono font-bold tracking-wider text-zinc-500 hover:text-white transition-colors">
      {showPw ? 'HIDE' : 'SHOW'}
    </button>
  </div>
);

const FEATURES = [
  { icon: Building2,   title: 'Organization Control', text: 'Create, suspend, and oversee every tenant on the platform.' },
  { icon: CreditCard,  title: 'Billing & Plans',      text: 'Track subscriptions, MRR, and plan changes across accounts.' },
  { icon: ShieldCheck, title: 'Platform Oversight',   text: 'Full visibility into users, activity, and account health.' },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [showForgot, setShowForgot] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const triggerError = (msg) => { setError(msg); setShake(true); setTimeout(() => setShake(false), 600); };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      triggerError(err.message || 'Invalid credentials. Please try again.');
    } finally { setLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}${ENDPOINTS.AUTH.FORGOT_PASSWORD}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || 'Request failed');
      setSuccessDialog(true);
    } catch (err) { triggerError(err.message); }
    finally { setLoading(false); }
  };

  const switchToForgot = () => { setForgotEmail(email); setShowForgot(true); setError(''); };
  const switchToLogin = () => { setShowForgot(false); setSuccessDialog(false); setError(''); };

  return (
    <div className="min-h-screen flex bg-[#09090b] text-[#fafafa] font-sans overflow-x-hidden relative">

      {/* ── LEFT brand panel ── */}
      <aside className="relative hidden lg:flex flex-col justify-between w-[50%] xl:w-[52%] bg-[#09090b] p-12 xl:p-16 border-r border-white/5 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(59,130,246,0.06),transparent_50%),radial-gradient(circle_at_80%_60%,rgba(168,85,247,0.01),transparent_50%)] pointer-events-none z-0" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.003)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.003)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none z-0" />
        <GridDots />

        <div className="flex items-center gap-3.5 relative z-10">
          <img src={LOGO_URL} alt="NexCarat Logo" className="w-10 h-10 object-contain" />
          <div className="flex flex-col leading-tight">
            <span className="font-display font-bold text-2xl tracking-tight text-white">NexCarat</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-blue-400">Admin Console</span>
          </div>
        </div>

        <div className="max-w-lg space-y-10 relative z-10 my-auto">
          <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border border-blue-500/20 bg-blue-500/5 text-[11px] font-mono tracking-wider uppercase text-blue-400">
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            Platform Operations
          </div>

          <h1 className="font-display font-extrabold text-4xl xl:text-5xl tracking-tight leading-[1.1] bg-gradient-to-b from-white via-zinc-200 to-zinc-500 bg-clip-text text-transparent">
            Every organization.<br />
            <span className="text-blue-500 italic font-semibold">One console.</span>
          </h1>

          <p className="text-base text-zinc-400 leading-relaxed">
            Manage every NexCarat organization from one place: onboarding, subscriptions, users, and account health, all in a single operator console.
          </p>

          <ul className="space-y-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <li key={i} className="flex items-start gap-4 text-sm text-zinc-450 animate-fadeIn" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/10 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <Icon size={15} className="text-blue-400" />
                  </div>
                  <div>
                    <span className="font-semibold text-zinc-200 block mb-0.5 text-sm sm:text-base">{f.title}</span>
                    <span className="text-zinc-500 leading-relaxed text-xs sm:text-sm">{f.text}</span>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="grid grid-cols-3 gap-4 pt-8 border-t border-white/5">
            <AnimatedStat target="10" suffix="+" label="Organizations" />
            <AnimatedStat target="50" suffix="+" label="Active users" />
            <AnimatedStat target="99.9" suffix="%" label="Uptime" />
          </div>
        </div>

        <div className="text-xs font-mono text-zinc-600 relative z-10">© 2026 NEXCARAT · ADMIN CONSOLE</div>
      </aside>

      {/* ── RIGHT form panel ── */}
      <main className="flex-1 flex items-center justify-center p-8 sm:p-16 md:p-20 bg-[#09090b] overflow-y-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_85%,rgba(59,130,246,0.03),transparent_50%)] pointer-events-none z-0" />

        <div className="w-full max-w-md space-y-10 relative z-10 py-10">

          {/* Forgot password */}
          <Transition show={showForgot}>
            <div className="transition duration-200 ease-out data-[closed]:opacity-0 data-[closed]:translate-y-2">
              {!successDialog ? (
                <div className="space-y-8">
                  <button onClick={switchToLogin} className="flex items-center gap-2 text-sm font-mono text-zinc-400 hover:text-white transition-colors bg-transparent border-0 p-0 cursor-pointer">← Back to sign in</button>
                  <div className="space-y-3">
                    <h2 className="font-display font-extrabold text-3xl text-white tracking-tight">Forgot password?</h2>
                    <p className="text-sm text-zinc-400">Enter your email and we'll send you a password reset link.</p>
                  </div>
                  <form onSubmit={handleForgot} className="space-y-6">
                    <Field className="space-y-2 text-left">
                      <Label className="text-xs font-mono font-semibold tracking-wider uppercase text-zinc-400">Email address</Label>
                      <Input as="input" type="email" placeholder="you@nexcarat.com"
                        className="w-full px-4 py-3 bg-zinc-900/50 border border-white/5 focus:border-blue-500/50 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all font-sans"
                        value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required autoFocus />
                    </Field>
                    {error && <ErrorBox shake={shake}>{error}</ErrorBox>}
                    <button type="submit" disabled={loading}
                      className="w-full py-3 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-sm font-semibold shadow-[0_4px_16px_rgba(59,130,246,0.25)] hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-pointer border-0">
                      {loading ? <><Spinner /> Sending…</> : 'Send reset link →'}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="text-center space-y-8">
                  <div className="w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto text-blue-400"><Mail size={26} /></div>
                  <div className="space-y-3">
                    <h3 className="font-display font-extrabold text-2xl text-white tracking-tight">Check your inbox</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed">We sent a reset link to <strong className="text-zinc-200">{forgotEmail}</strong>. Click the link inside to set a new password.</p>
                  </div>
                  <button onClick={switchToLogin} className="w-full py-3 rounded-lg border border-white/5 bg-zinc-900/50 hover:bg-zinc-800 text-zinc-300 text-sm font-semibold transition-all cursor-pointer">← Back to sign in</button>
                </div>
              )}
            </div>
          </Transition>

          {/* Sign in */}
          <Transition show={!showForgot}>
            <div className="transition duration-200 ease-out data-[closed]:opacity-0 data-[closed]:translate-y-2 space-y-8">
              <div className="space-y-3 text-left">
                <h2 className="font-display font-extrabold text-3xl text-white tracking-tight">Welcome back</h2>
                <p className="text-sm text-zinc-400">Sign in to the NexCarat platform console.</p>
              </div>

              <form onSubmit={handleSignIn} className="space-y-6">
                <Field className="space-y-2 text-left">
                  <Label className="text-xs font-mono font-semibold tracking-wider uppercase text-zinc-400">Email address</Label>
                  <Input as="input" type="email" placeholder="you@nexcarat.com"
                    className="w-full px-4 py-3 bg-zinc-900/50 border border-white/5 focus:border-blue-500/50 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all font-sans"
                    value={email} onChange={e => setEmail(e.target.value)} required />
                </Field>

                <Field className="space-y-2 text-left">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs font-mono font-semibold tracking-wider uppercase text-zinc-400">Password</Label>
                    <button type="button" onClick={switchToForgot} className="text-xs font-mono font-bold tracking-wider text-blue-500 hover:text-blue-400 transition-colors bg-transparent border-0 p-0 cursor-pointer">FORGOT?</button>
                  </div>
                  <PwInput showPw={showPw} onToggle={() => setShowPw(v => !v)} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required />
                </Field>

                <div className="flex items-center text-left py-1">
                  <Checkbox checked={remember} onChange={setRemember}
                    className="group block size-5 rounded border border-white/10 bg-zinc-950/60 data-[checked]:bg-blue-500 data-[checked]:border-blue-500 transition-all cursor-pointer flex items-center justify-center">
                    <Check className="size-3.5 text-white stroke-[3] opacity-0 group-data-[checked]:opacity-100 transition-opacity" />
                  </Checkbox>
                  <span onClick={() => setRemember(!remember)} className="ml-3 text-sm text-zinc-400 cursor-pointer select-none">Remember this device</span>
                </div>

                {error && <ErrorBox shake={shake}>{error}</ErrorBox>}

                <button type="submit" disabled={loading}
                  className="w-full py-3 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-sm font-semibold shadow-[0_4px_16px_rgba(59,130,246,0.25)] hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-pointer border-0">
                  {loading ? <><Spinner /> Signing in…</> : 'Sign in to console →'}
                </button>
              </form>

              <p className="text-xs text-zinc-600 text-center">Admin access only. Contact the platform team if you need an account.</p>
            </div>
          </Transition>
        </div>
      </main>

      {/* Password reset success dialog */}
      <Dialog open={successDialog} onClose={() => { setSuccessDialog(false); switchToLogin(); }} className="relative z-50">
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-2xl bg-zinc-900 border border-white/5 p-8 text-center space-y-6 shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto text-blue-400"><Mail size={26} /></div>
            <div className="space-y-3">
              <DialogTitle className="font-display font-extrabold text-2xl text-white tracking-tight">Check your inbox</DialogTitle>
              <p className="text-sm text-zinc-400 leading-relaxed">We sent a password reset link to <strong className="text-zinc-200">{forgotEmail}</strong>. Click the link to choose a new password.</p>
            </div>
            <button onClick={() => { setSuccessDialog(false); switchToLogin(); }}
              className="w-full py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold shadow-[0_4px_16px_rgba(59,130,246,0.25)] transition-all cursor-pointer border-0">Back to sign in</button>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}
